import { PAGE_CONTAINER_MAX_WIDTH } from '../constants/documentTheme';
import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Container,
  Grid,
  Stack,
  TextField,
  Typography,
  Alert,
  Chip,
  CircularProgress,
  Card,
  CardContent,
} from '@mui/material';
import {
  GroupsOutlined as GroupsIcon,
  MapOutlined as MapIcon,
  LocalOfferOutlined as DealsIcon,
  ChatOutlined as ChatIcon,
  StorefrontOutlined as MarketplaceIcon,
  DashboardOutlined as DashboardIcon,
  PeopleOutline as ClientsIcon,
  CalendarMonthOutlined as CalendarIcon,
  ArrowForward as ArrowIcon,
  Apple as AppleIcon,
} from '@mui/icons-material';
import { Link as RouterLink, Navigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEOHead from '../components/SEOHead';
import CalendlyEmbed from '../components/CalendlyEmbed';
import RevealPanel from '../components/RevealPanel';
import api from '../config/api';
import { isMobileApp } from '../utils/platformDetection';
import {
  APP_STORE_URL,
  PLAY_STORE_URL,
  SHOWS_HUB,
  mapApiShowToEvent,
} from '../config/marketingEvents';
import {
  submitPartnerInterest,
  submitPartnerDemoRequest,
  getPartnerInterestErrorMessage,
} from '../utils/partnerInterest';

const getNested = (obj, path) =>
  path.split('.').reduce((acc, key) => (acc == null ? undefined : acc[key]), obj);

const TRAVELER_IMAGES = [
  'https://images.unsplash.com/photo-1529156069898-49953e39b3ac?auto=format&fit=crop&w=1200&q=80',
  'https://images.unsplash.com/photo-1488646953014-85cb44e25828?auto=format&fit=crop&w=1200&q=80',
  'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?auto=format&fit=crop&w=1200&q=80',
  'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=1200&q=80',
];

const TRAVELER_ICONS = [GroupsIcon, MapIcon, DealsIcon, ChatIcon];
const TRAVELER_KEYS = ['groups', 'itineraries', 'deals', 'chat'];

const AGENT_IMAGES = [
  'https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=1200&q=80',
  'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?auto=format&fit=crop&w=1200&q=80',
  'https://images.unsplash.com/photo-1521737711867-e3b97375f902?auto=format&fit=crop&w=1200&q=80',
];

const AGENT_ICONS = [MarketplaceIcon, DashboardIcon, ClientsIcon];
const AGENT_KEYS = ['marketplace', 'dashboard', 'clients'];

const GOLD_PROMO_SX = {
  background: 'linear-gradient(180deg, #F5E6B8 0%, #D4AF37 48%, #B8860B 100%)',
  color: '#3D2A00',
  fontWeight: 700,
  border: '1px solid #E8C547',
  boxShadow: '0 0 14px rgba(212, 175, 55, 0.45)',
};

const demoFieldSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: '#fff',
    borderRadius: 2,
  },
  '& .MuiInputBase-input': {
    color: '#111',
    WebkitTextFillColor: '#111',
    '&:-webkit-autofill': {
      WebkitBoxShadow: '0 0 0 1000px #fff inset',
      WebkitTextFillColor: '#111',
      caretColor: '#111',
    },
  },
};

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
};

const AppDownloadButtons = ({ t }) => (
  <Stack
    direction={{ xs: 'column', sm: 'row' }}
    spacing={2}
    justifyContent="center"
    alignItems="center"
  >
    <Button
      component="a"
      href={PLAY_STORE_URL}
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        backgroundColor: '#000',
        color: 'white',
        px: 2.5,
        py: 1,
        borderRadius: 2,
        textTransform: 'none',
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        minWidth: 180,
        '&:hover': { backgroundColor: '#1a1a1a' },
      }}
    >
      <Box
        component="img"
        src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath fill='%23fff' d='M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z'/%3E%3C/svg%3E"
        alt="Google Play"
        sx={{ width: 22, height: 22 }}
      />
      <Box sx={{ textAlign: 'left' }}>
        <Typography sx={{ fontSize: '0.65rem', lineHeight: 1, opacity: 0.8 }}>
          {t('eventPages.shared.download.getItOn')}
        </Typography>
        <Typography sx={{ fontWeight: 700, fontSize: '1rem', lineHeight: 1.2 }}>
          Google Play
        </Typography>
      </Box>
    </Button>

    <Button
      component="a"
      href={APP_STORE_URL}
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        backgroundColor: '#000',
        color: '#fff',
        px: 2.5,
        py: 1,
        borderRadius: 2,
        textTransform: 'none',
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        minWidth: 180,
        '&:hover': { backgroundColor: '#333' },
      }}
    >
      <AppleIcon sx={{ fontSize: 26 }} />
      <Box sx={{ textAlign: 'left' }}>
        <Typography sx={{ fontSize: '0.65rem', lineHeight: 1, opacity: 0.8 }}>
          {t('eventPages.shared.download.getItOn')}
        </Typography>
        <Typography sx={{ fontWeight: 700, fontSize: '1rem', lineHeight: 1.2 }}>
          App Store
        </Typography>
      </Box>
    </Button>
  </Stack>
);

/**
 * Shared marketing page for trade-show events (CruiseWorld, …).
 * Loads show copy from /api/marketing/shows/:slug (admin-editable).
 * @param {{ eventId?: string }} props
 */
const EventMarketingPage = ({ eventId: eventIdProp } = {}) => {
  const { t } = useTranslation();
  const { slug: slugParam } = useParams();
  const slug = eventIdProp || slugParam;

  const shouldShowAppDownload = !isMobileApp();

  const [event, setEvent] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const [demoForm, setDemoForm] = useState({
    name: '',
    business: '',
    email: '',
    phone: '',
    location: '',
    businessDescription: '',
  });
  const [demoHoneypot, setDemoHoneypot] = useState('');
  const [demoLoading, setDemoLoading] = useState(false);
  const [demoError, setDemoError] = useState('');
  const [demoReady, setDemoReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!slug) {
      setEvent(null);
      setPageLoading(false);
      return undefined;
    }
    setPageLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/marketing/shows/${slug}`);
        if (!cancelled) {
          setEvent(mapApiShowToEvent(data?.show));
        }
      } catch {
        if (!cancelled) setEvent(null);
      } finally {
        if (!cancelled) setPageLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const te = (key) => getNested(event?.copy, key) || '';
  const ts = (key, options) => t(`eventPages.shared.${key}`, options);

  if (pageLoading) {
    return (
      <Box sx={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress sx={{ color: '#591716' }} />
      </Box>
    );
  }

  if (!event) {
    return <Navigate to={SHOWS_HUB.path} replace />;
  }

  const handleInterestSubmit = async (submitEvent) => {
    submitEvent.preventDefault();
    setError('');
    setLoading(true);

    try {
      await submitPartnerInterest(api, {
        email,
        companyName,
        website_url: honeypot,
        source: event.source,
      });
      setSuccess(true);
      setEmail('');
      setCompanyName('');
    } catch (err) {
      setError(getPartnerInterestErrorMessage(err, t));
    } finally {
      setLoading(false);
    }
  };

  const handleDemoFieldChange = (field) => (changeEvent) => {
    setDemoForm((prev) => ({ ...prev, [field]: changeEvent.target.value }));
  };

  const handleDemoSubmit = async (submitEvent) => {
    submitEvent.preventDefault();
    setDemoError('');
    setDemoLoading(true);

    try {
      await submitPartnerDemoRequest(api, {
        ...demoForm,
        website_url: demoHoneypot,
      });
      setDemoReady(true);
    } catch (err) {
      setDemoError(getPartnerInterestErrorMessage(err, t));
    } finally {
      setDemoLoading(false);
    }
  };

  return (
    <Box sx={{ bgcolor: '#fafafa', minHeight: '100vh' }}>
      <SEOHead
        title={te('seo.title')}
        description={te('seo.description')}
        keywords={te('seo.keywords')}
        canonical={`https://dejavunomad.com${event.path}`}
      />

      <Box
        sx={{
          position: 'relative',
          minHeight: { xs: '78vh', md: '88vh' },
          display: 'flex',
          alignItems: 'center',
          color: '#fff',
          overflow: 'hidden',
          backgroundImage: `linear-gradient(135deg, rgba(17,17,17,0.78) 0%, rgba(89,23,22,0.7) 55%, rgba(49,56,72,0.6) 100%), url(${event.heroImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH} sx={{ py: { xs: 10, md: 14 }, position: 'relative', zIndex: 1 }}>
          <Box
            sx={{
              mb: { xs: 3, md: 4 },
              '@keyframes eventBrandIn': {
                from: { opacity: 0, transform: 'translateY(18px)' },
                to: { opacity: 1, transform: 'translateY(0)' },
              },
              animation: 'eventBrandIn 0.7s ease-out both',
            }}
          >
            <Typography
              component="p"
              sx={{
                display: 'inline-block',
                mb: 1.5,
                px: 1.75,
                py: 0.75,
                borderRadius: 1,
                letterSpacing: { xs: 3, md: 5 },
                fontWeight: 800,
                fontSize: { xs: '0.95rem', sm: '1.1rem', md: '1.25rem' },
                textTransform: 'uppercase',
                color: '#1a1208',
                bgcolor: '#E8C547',
                boxShadow: '0 8px 28px rgba(232, 197, 71, 0.35)',
              }}
            >
              {te('hero.event')}
            </Typography>
            <Typography
              component="p"
              sx={{
                fontFamily: 'var(--font-display), Playfair Display, Georgia, serif',
                fontWeight: 700,
                fontSize: { xs: '2.75rem', sm: '3.5rem', md: '4.5rem' },
                lineHeight: 1.05,
                letterSpacing: '-0.02em',
                color: '#fff',
                textShadow: '0 4px 28px rgba(0,0,0,0.45)',
                mb: 1,
              }}
            >
              Déjà Vu Nomad
            </Typography>
            <Box
              sx={{
                width: { xs: 72, md: 96 },
                height: 4,
                borderRadius: 2,
                background: 'linear-gradient(90deg, #E8C547 0%, rgba(232,197,71,0.15) 100%)',
                mb: { xs: 2.5, md: 3 },
              }}
            />
          </Box>
          <Typography
            variant="h2"
            component="h1"
            sx={{
              fontFamily: 'var(--font-display), Playfair Display, serif',
              fontWeight: 600,
              fontSize: { xs: '1.65rem', sm: '2rem', md: '2.5rem' },
              lineHeight: 1.2,
              maxWidth: 640,
              mb: 2,
              color: 'rgba(255,255,255,0.95)',
              animation: 'eventBrandIn 0.7s ease-out 0.12s both',
            }}
          >
            {te('hero.title')}
          </Typography>
          <Typography
            variant="h6"
            sx={{
              maxWidth: 560,
              mb: 2.5,
              opacity: 0.9,
              fontWeight: 400,
              lineHeight: 1.6,
              fontSize: { xs: '1rem', md: '1.15rem' },
              animation: 'eventBrandIn 0.7s ease-out 0.2s both',
            }}
          >
            {te('hero.subtitle')}
          </Typography>
          <Chip
            label={ts('foundingPromo.title')}
            onClick={() => scrollToId('event-agents')}
            sx={{
              ...GOLD_PROMO_SX,
              mb: 3,
              height: 'auto',
              py: 0.85,
              px: 0.5,
              cursor: 'pointer',
              fontSize: { xs: '0.8rem', md: '0.9rem' },
              animation: 'eventBrandIn 0.7s ease-out 0.24s both',
              '& .MuiChip-label': { whiteSpace: 'normal', lineHeight: 1.35 },
            }}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap" useFlexGap>
            <Button
              variant="contained"
              size="large"
              component={RouterLink}
              to="/register"
              endIcon={<ArrowIcon />}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                px: 4,
                py: 1.5,
                borderRadius: 2,
                bgcolor: '#fff',
                color: '#591716',
                '&:hover': { bgcolor: '#f5f5f5' },
              }}
            >
              {ts('hero.travelerCta')}
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => scrollToId('event-download')}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                px: 4,
                py: 1.5,
                borderRadius: 2,
                borderColor: 'rgba(255,255,255,0.7)',
                color: '#fff',
                '&:hover': {
                  borderColor: '#fff',
                  bgcolor: 'rgba(255,255,255,0.1)',
                },
              }}
            >
              {ts('hero.downloadCta')}
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => scrollToId('event-agents')}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                px: 4,
                py: 1.5,
                borderRadius: 2,
                borderColor: 'rgba(255,255,255,0.7)',
                color: '#fff',
                '&:hover': {
                  borderColor: '#fff',
                  bgcolor: 'rgba(255,255,255,0.1)',
                },
              }}
            >
              {ts('hero.partnerCta')}
            </Button>
          </Stack>
          <Typography
            variant="body2"
            sx={{ mt: 2, opacity: 0.8, maxWidth: 480 }}
          >
            {ts('hero.travelerHint')}
          </Typography>
        </Container>
      </Box>

      <Box id="event-travelers" sx={{ py: { xs: 6, md: 8 }, bgcolor: '#fff', textAlign: 'center' }}>
        <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH}>
          <RevealPanel>
            <Chip
              label={ts('travelers.badge')}
              sx={{ mb: 2, fontWeight: 600, bgcolor: 'rgba(89,23,22,0.08)', color: '#591716' }}
            />
            <Typography
              variant="h3"
              component="h2"
              sx={{
                fontFamily: 'var(--font-display), Playfair Display, serif',
                fontWeight: 700,
                fontSize: { xs: '1.9rem', md: '2.5rem' },
                mb: 1.5,
                color: '#111',
              }}
            >
              {ts('travelers.title')}
            </Typography>
            <Typography sx={{ color: '#465066', maxWidth: 560, mx: 'auto', mb: 3 }}>
              {ts('travelers.subtitle')}
            </Typography>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              justifyContent="center"
              alignItems="center"
            >
              <Button
                variant="contained"
                color="primary"
                size="large"
                component={RouterLink}
                to="/register"
                endIcon={<ArrowIcon />}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  px: 3.5,
                  py: 1.35,
                  borderRadius: 2,
                }}
              >
                {ts('travelers.signupCta')}
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => scrollToId('event-download')}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3.5,
                  py: 1.35,
                  borderRadius: 2,
                  borderColor: '#591716',
                  color: '#591716',
                }}
              >
                {ts('hero.downloadCta')}
              </Button>
            </Stack>
            <Typography
              variant="body2"
              sx={{ color: '#6b7280', mt: 2, maxWidth: 440, mx: 'auto' }}
            >
              {ts('travelers.signupHint')}
            </Typography>
          </RevealPanel>
        </Container>
      </Box>

      <Box>
        {TRAVELER_KEYS.map((key, index) => {
          const Icon = TRAVELER_ICONS[index];
          const imageLeft = index % 2 === 0;
          const direction = imageLeft ? 'left' : 'right';

          return (
            <Box
              key={key}
              sx={{
                py: { xs: 6, md: 10 },
                bgcolor: index % 2 === 0 ? '#f4f1ef' : '#fff',
              }}
            >
              <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH}>
                <RevealPanel direction={direction}>
                  <Grid
                    container
                    spacing={{ xs: 4, md: 6 }}
                    alignItems="center"
                    direction={imageLeft ? 'row' : 'row-reverse'}
                  >
                    <Grid item xs={12} md={6}>
                      <Box
                        sx={{
                          position: 'relative',
                          borderRadius: 4,
                          overflow: 'hidden',
                          boxShadow: '0 20px 50px rgba(17,17,17,0.18)',
                          minHeight: { xs: 240, md: 360 },
                        }}
                      >
                        <Box
                          component="img"
                          src={TRAVELER_IMAGES[index]}
                          alt={ts(`travelers.benefits.${key}.title`)}
                          sx={{
                            width: '100%',
                            height: '100%',
                            minHeight: { xs: 240, md: 360 },
                            objectFit: 'cover',
                            display: 'block',
                          }}
                        />
                        <Box
                          sx={{
                            position: 'absolute',
                            inset: 0,
                            background:
                              'linear-gradient(180deg, transparent 40%, rgba(17,17,17,0.45) 100%)',
                          }}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box
                        sx={{
                          width: 56,
                          height: 56,
                          borderRadius: 2,
                          bgcolor: 'rgba(89, 23, 22, 0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 2,
                        }}
                      >
                        <Icon sx={{ fontSize: 30, color: '#591716' }} />
                      </Box>
                      <Typography
                        variant="overline"
                        sx={{ color: '#591716', fontWeight: 700, letterSpacing: 1.2 }}
                      >
                        {ts('travelers.benefits.label', { number: index + 1 })}
                      </Typography>
                      <Typography
                        variant="h3"
                        component="h2"
                        sx={{
                          fontFamily: 'var(--font-display), Playfair Display, serif',
                          fontWeight: 700,
                          fontSize: { xs: '1.75rem', md: '2.4rem' },
                          mb: 2,
                          color: '#111',
                        }}
                      >
                        {ts(`travelers.benefits.${key}.title`)}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          fontSize: '1.1rem',
                          lineHeight: 1.75,
                          color: '#465066',
                          mb: 3,
                        }}
                      >
                        {ts(`travelers.benefits.${key}.description`)}
                      </Typography>
                      <Button
                        variant="text"
                        endIcon={<ArrowIcon />}
                        component={RouterLink}
                        to="/register"
                        sx={{ textTransform: 'none', fontWeight: 700, color: '#591716' }}
                      >
                        {ts('travelers.signupCta')}
                      </Button>
                    </Grid>
                  </Grid>
                </RevealPanel>
              </Container>
            </Box>
          );
        })}
      </Box>

      {shouldShowAppDownload && (
        <Box
          id="event-download"
          sx={{
            py: { xs: 8, md: 10 },
            background: 'linear-gradient(135deg, #111111 0%, #591716 55%, #313848 100%)',
            color: '#fff',
            textAlign: 'center',
          }}
        >
          <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH}>
            <RevealPanel>
              <Typography
                variant="h3"
                component="h2"
                sx={{
                  fontFamily: 'var(--font-display), Playfair Display, serif',
                  fontWeight: 700,
                  fontSize: { xs: '1.9rem', md: '2.5rem' },
                  mb: 1.5,
                }}
              >
                {ts('download.title')}
              </Typography>
              <Typography sx={{ opacity: 0.9, maxWidth: 520, mx: 'auto', mb: 4 }}>
                {ts('download.subtitle')}
              </Typography>
              <AppDownloadButtons t={t} />
              <Typography sx={{ opacity: 0.85, mt: 3, mb: 1.5 }}>
                {ts('download.webSignup')}
              </Typography>
              <Button
                variant="outlined"
                component={RouterLink}
                to="/register"
                endIcon={<ArrowIcon />}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  color: '#fff',
                  borderColor: 'rgba(255,255,255,0.55)',
                  borderRadius: 2,
                  px: 3,
                  '&:hover': {
                    borderColor: '#fff',
                    bgcolor: 'rgba(255,255,255,0.1)',
                  },
                }}
              >
                {ts('travelers.signupCta')}
              </Button>
            </RevealPanel>
          </Container>
        </Box>
      )}

      <Box id="event-agents" sx={{ py: { xs: 6, md: 8 }, bgcolor: '#fff', textAlign: 'center' }}>
        <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH}>
          <RevealPanel direction="right">
            <Chip
              label={ts('agents.badge')}
              sx={{ mb: 2, fontWeight: 600, bgcolor: 'rgba(89,23,22,0.08)', color: '#591716' }}
            />
            <Typography
              variant="h3"
              component="h2"
              sx={{
                fontFamily: 'var(--font-display), Playfair Display, serif',
                fontWeight: 700,
                fontSize: { xs: '1.9rem', md: '2.5rem' },
                mb: 1.5,
                color: '#111',
              }}
            >
              {te('agents.title')}
            </Typography>
            <Typography sx={{ color: '#465066', maxWidth: 560, mx: 'auto', mb: 3 }}>
              {te('agents.subtitle')}
            </Typography>
            <Card
              sx={{
                maxWidth: 640,
                mx: 'auto',
                textAlign: 'left',
                borderRadius: 3,
                border: '1px solid #E8C547',
                background: 'linear-gradient(180deg, rgba(245,230,184,0.35) 0%, #fff 55%)',
                boxShadow: '0 10px 28px rgba(212, 175, 55, 0.18)',
              }}
            >
              <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                <Chip
                  size="small"
                  label={ts('foundingPromo.badge')}
                  sx={{ ...GOLD_PROMO_SX, mb: 1.5 }}
                />
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: '#111' }}>
                  {ts('foundingPromo.title')}
                </Typography>
                <Typography variant="body2" sx={{ color: '#465066', mb: 2, lineHeight: 1.7 }}>
                  {ts('foundingPromo.body')}
                </Typography>
                <Button
                  variant="contained"
                  component={RouterLink}
                  to="/partner/signup"
                  endIcon={<ArrowIcon />}
                  sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2 }}
                >
                  {ts('foundingPromo.cta')}
                </Button>
              </CardContent>
            </Card>
          </RevealPanel>
        </Container>
      </Box>

      <Box>
        {AGENT_KEYS.map((key, index) => {
          const Icon = AGENT_ICONS[index];
          const imageLeft = index % 2 === 0;
          const direction = imageLeft ? 'left' : 'right';

          return (
            <Box
              key={key}
              sx={{
                py: { xs: 6, md: 10 },
                bgcolor: index % 2 === 0 ? '#f4f1ef' : '#fff',
              }}
            >
              <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH}>
                <RevealPanel direction={direction}>
                  <Grid
                    container
                    spacing={{ xs: 4, md: 6 }}
                    alignItems="center"
                    direction={imageLeft ? 'row' : 'row-reverse'}
                  >
                    <Grid item xs={12} md={6}>
                      <Box
                        sx={{
                          position: 'relative',
                          borderRadius: 4,
                          overflow: 'hidden',
                          boxShadow: '0 20px 50px rgba(17,17,17,0.18)',
                          minHeight: { xs: 240, md: 360 },
                        }}
                      >
                        <Box
                          component="img"
                          src={AGENT_IMAGES[index]}
                          alt={ts(`agents.benefits.${key}.title`)}
                          sx={{
                            width: '100%',
                            height: '100%',
                            minHeight: { xs: 240, md: 360 },
                            objectFit: 'cover',
                            display: 'block',
                          }}
                        />
                        <Box
                          sx={{
                            position: 'absolute',
                            inset: 0,
                            background:
                              'linear-gradient(180deg, transparent 40%, rgba(17,17,17,0.45) 100%)',
                          }}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box
                        sx={{
                          width: 56,
                          height: 56,
                          borderRadius: 2,
                          bgcolor: 'rgba(89, 23, 22, 0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 2,
                        }}
                      >
                        <Icon sx={{ fontSize: 30, color: '#591716' }} />
                      </Box>
                      <Typography
                        variant="overline"
                        sx={{ color: '#591716', fontWeight: 700, letterSpacing: 1.2 }}
                      >
                        {ts('agents.benefits.label', { number: index + 1 })}
                      </Typography>
                      <Typography
                        variant="h3"
                        component="h2"
                        sx={{
                          fontFamily: 'var(--font-display), Playfair Display, serif',
                          fontWeight: 700,
                          fontSize: { xs: '1.75rem', md: '2.4rem' },
                          mb: 2,
                          color: '#111',
                        }}
                      >
                        {ts(`agents.benefits.${key}.title`)}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          fontSize: '1.1rem',
                          lineHeight: 1.75,
                          color: '#465066',
                          mb: 3,
                        }}
                      >
                        {ts(`agents.benefits.${key}.description`)}
                      </Typography>
                      <Button
                        variant="text"
                        endIcon={<ArrowIcon />}
                        component={RouterLink}
                        to="/partner/signup"
                        sx={{ textTransform: 'none', fontWeight: 700, color: '#591716' }}
                      >
                        {ts('agents.benefits.cta')}
                      </Button>
                    </Grid>
                  </Grid>
                </RevealPanel>
              </Container>
            </Box>
          );
        })}
      </Box>

      <Box
        id="event-interest"
        sx={{
          py: { xs: 8, md: 10 },
          background: 'linear-gradient(135deg, #111111 0%, #591716 55%, #313848 100%)',
          color: '#fff',
        }}
      >
        <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH}>
          <RevealPanel direction="left">
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography
                variant="h3"
                component="h2"
                sx={{
                  fontFamily: 'var(--font-display), Playfair Display, serif',
                  fontWeight: 700,
                  fontSize: { xs: '1.9rem', md: '2.5rem' },
                  mb: 1.5,
                }}
              >
                {te('interest.title')}
              </Typography>
              <Typography sx={{ opacity: 0.9, maxWidth: 560, mx: 'auto' }}>
                {te('interest.subtitle')}
              </Typography>
            </Box>

            {success ? (
              <Alert
                severity="success"
                sx={{ maxWidth: 480, mx: 'auto', borderRadius: 2 }}
              >
                {ts('interest.success')}
              </Alert>
            ) : (
              <Box
                component="form"
                onSubmit={handleInterestSubmit}
                sx={{
                  maxWidth: 480,
                  mx: 'auto',
                  p: { xs: 3, md: 4 },
                  borderRadius: 3,
                  bgcolor: '#fff',
                  boxShadow: '0 18px 50px rgba(0,0,0,0.28)',
                  color: '#111',
                }}
              >
                <TextField
                  name="website_url"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  sx={{
                    position: 'absolute',
                    left: -9999,
                    width: 1,
                    height: 1,
                    overflow: 'hidden',
                  }}
                />
                <Stack spacing={2}>
                  <TextField
                    required
                    fullWidth
                    type="email"
                    placeholder={ts('interest.emailLabel')}
                    aria-label={ts('interest.emailLabel')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        bgcolor: '#f8f9fa',
                      },
                    }}
                  />
                  <TextField
                    fullWidth
                    placeholder={ts('interest.companyLabel')}
                    aria-label={ts('interest.companyLabel')}
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    disabled={loading}
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        bgcolor: '#f8f9fa',
                      },
                    }}
                  />
                  {error && (
                    <Alert severity="error" sx={{ borderRadius: 2 }}>
                      {error}
                    </Alert>
                  )}
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    size="large"
                    disabled={loading}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 700,
                      py: 1.4,
                      borderRadius: 2,
                      mt: 0.5,
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={22} color="inherit" />
                    ) : (
                      te('interest.submit')
                    )}
                  </Button>
                </Stack>
              </Box>
            )}
          </RevealPanel>
        </Container>
      </Box>

      <Box id="event-demo" sx={{ py: { xs: 8, md: 10 }, bgcolor: '#fff' }}>
        <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH}>
          <RevealPanel direction="right">
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Chip
                icon={<CalendarIcon />}
                label={te('demo.badge')}
                sx={{ mb: 2, fontWeight: 600, bgcolor: 'rgba(89,23,22,0.08)', color: '#591716' }}
              />
              <Typography
                variant="h3"
                component="h2"
                sx={{
                  fontFamily: 'var(--font-display), Playfair Display, serif',
                  fontWeight: 700,
                  fontSize: { xs: '1.9rem', md: '2.5rem' },
                  mb: 1.5,
                  color: '#111',
                }}
              >
                {te('demo.title')}
              </Typography>
              <Typography sx={{ color: '#465066', maxWidth: 560, mx: 'auto', mb: 4 }}>
                {ts('demo.subtitle')}
              </Typography>
            </Box>
            {demoReady ? (
              <Box>
                <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
                  {ts('demo.ready')}
                </Alert>
                <CalendlyEmbed
                  prefill={{
                    name: demoForm.name,
                    email: demoForm.email,
                  }}
                />
              </Box>
            ) : (
              <Box
                component="form"
                onSubmit={handleDemoSubmit}
                sx={{
                  maxWidth: 560,
                  mx: 'auto',
                  p: { xs: 3, md: 4 },
                  borderRadius: 3,
                  bgcolor: '#f8f9fa',
                  border: '1px solid rgba(0,0,0,0.08)',
                }}
              >
                <TextField
                  name="website_url"
                  value={demoHoneypot}
                  onChange={(e) => setDemoHoneypot(e.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  sx={{
                    position: 'absolute',
                    left: -9999,
                    width: 1,
                    height: 1,
                    overflow: 'hidden',
                  }}
                />
                <Stack spacing={2}>
                  <TextField
                    required
                    fullWidth
                    label={ts('demo.fields.name')}
                    value={demoForm.name}
                    onChange={handleDemoFieldChange('name')}
                    disabled={demoLoading}
                    sx={demoFieldSx}
                  />
                  <TextField
                    required
                    fullWidth
                    label={ts('demo.fields.business')}
                    value={demoForm.business}
                    onChange={handleDemoFieldChange('business')}
                    disabled={demoLoading}
                    sx={demoFieldSx}
                  />
                  <TextField
                    required
                    fullWidth
                    type="email"
                    label={ts('demo.fields.email')}
                    value={demoForm.email}
                    onChange={handleDemoFieldChange('email')}
                    disabled={demoLoading}
                    sx={demoFieldSx}
                  />
                  <TextField
                    fullWidth
                    type="tel"
                    label={ts('demo.fields.phone')}
                    value={demoForm.phone}
                    onChange={handleDemoFieldChange('phone')}
                    disabled={demoLoading}
                    sx={demoFieldSx}
                  />
                  <TextField
                    fullWidth
                    label={ts('demo.fields.location')}
                    value={demoForm.location}
                    onChange={handleDemoFieldChange('location')}
                    disabled={demoLoading}
                    sx={demoFieldSx}
                  />
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    label={ts('demo.fields.businessDescription')}
                    value={demoForm.businessDescription}
                    onChange={handleDemoFieldChange('businessDescription')}
                    disabled={demoLoading}
                    sx={demoFieldSx}
                  />
                  {demoError && (
                    <Alert severity="error" sx={{ borderRadius: 2 }}>
                      {demoError}
                    </Alert>
                  )}
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    size="large"
                    disabled={demoLoading}
                    sx={{ textTransform: 'none', fontWeight: 700, py: 1.4, borderRadius: 2 }}
                  >
                    {demoLoading ? (
                      <CircularProgress size={22} color="inherit" />
                    ) : (
                      ts('demo.continue')
                    )}
                  </Button>
                </Stack>
              </Box>
            )}
          </RevealPanel>
        </Container>
      </Box>

      <Box
        sx={{
          py: { xs: 6, md: 8 },
          textAlign: 'center',
          bgcolor: '#f4f1ef',
          borderTop: '1px solid rgba(0,0,0,0.06)',
        }}
      >
        <Container maxWidth="sm">
          <Typography
            variant="h4"
            sx={{
              fontFamily: 'var(--font-display), Playfair Display, serif',
              fontWeight: 700,
              mb: 2,
            }}
          >
            {te('finalCta.title')}
          </Typography>
          <Typography sx={{ color: '#465066', mb: 2 }}>
            {te('finalCta.subtitle')}
          </Typography>
          <Chip
            label={ts('foundingPromo.title')}
            component={RouterLink}
            to="/partner/signup"
            clickable
            sx={{
              ...GOLD_PROMO_SX,
              mb: 3,
              height: 'auto',
              py: 0.85,
              cursor: 'pointer',
              fontSize: { xs: '0.8rem', md: '0.88rem' },
              '& .MuiChip-label': { whiteSpace: 'normal', lineHeight: 1.35 },
            }}
          />
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent="center"
          >
            <Button
              variant="contained"
              size="large"
              component={RouterLink}
              to="/register"
              endIcon={<ArrowIcon />}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                px: 4,
                py: 1.5,
                borderRadius: 2,
              }}
            >
              {ts('finalCta.travelerCta')}
            </Button>
            <Button
              variant="outlined"
              size="large"
              component={RouterLink}
              to="/partner/signup"
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                px: 4,
                py: 1.5,
                borderRadius: 2,
                borderColor: '#591716',
                color: '#591716',
              }}
            >
              {ts('finalCta.agentCta')}
            </Button>
          </Stack>
          <Button
            component={RouterLink}
            to={SHOWS_HUB.path}
            sx={{
              mt: 3,
              textTransform: 'none',
              fontWeight: 600,
              color: '#591716',
            }}
          >
            {ts('finalCta.seeAllEvents')}
          </Button>
        </Container>
      </Box>
    </Box>
  );
};

export default EventMarketingPage;
